import React from 'react';
import Spade from 'lucide-react/dist/esm/icons/spade.js';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle.js';
import Loader2 from 'lucide-react/dist/esm/icons/loader-2.js';
import Heart from 'lucide-react/dist/esm/icons/heart.js';
import { usePurchasePage } from './PurchasePageContext';
import { getSelectedPartner } from '../../../utils/partnerSelection';

const suitSymbols = { hearts: '♥', diamonds: '♦', clubs: '♣', spades: '♠' };
const suitColors = { hearts: 'text-red-600', diamonds: 'text-red-600', clubs: 'text-gray-900', spades: 'text-gray-900' };
const getRank = (val) => typeof val === 'string' && isNaN(val) ? val[0] : val;

export default function PurchaseCardSelectorSection() {
  // Inline card picker is retired — the dedicated /chase-the-ace-card page is
  // now the canonical Pick Your Card step in the CTA flow. Component is kept
  // (not deleted) so any DB-persisted layouts that reference this section type
  // don't break the registry; it just renders nothing.
  return null;
}

function _RetiredPurchaseCardSelectorSection() {
  const {
    gameType,
    availableCards,
    selectedCard,
    setSelectedCard,
    selectedCardNum,
    setSelectedCardNum,
    selectedPartner,
    purchasePageFieldLabel,
  } = usePurchasePage();

  const currentPartner = selectedPartner || getSelectedPartner();
  const partnerName = currentPartner?.name || currentPartner?.organization_name || currentPartner?.partnerName || '';

  // Only render for Chase the Ace; the dedicated /chase-the-ace-card page is
  // kept as a fallback (sticky bar still routes there if needed).
  if (gameType !== 'chaseTheAce') return null;

  const loading = availableCards.length === 0;

  const handleSelect = (id, num) => {
    setSelectedCard(id);
    setSelectedCardNum(num);
  };

  return (
    <div id="card-selector" className="bg-white dark:bg-gray-800 rounded-2xl p-4 md:p-6 shadow-lg mb-6">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Spade className="w-5 h-5 text-charity-primary" />
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Pick Your Envelope</h2>
        </div>
        {partnerName && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 dark:bg-emerald-950/40 text-xs font-semibold text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
            <Heart className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 fill-emerald-600/20" />
            <span className="truncate max-w-[200px]">
              {purchasePageFieldLabel ? `${purchasePageFieldLabel}: ` : ''}{partnerName}
            </span>
          </span>
        )}
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Select an envelope containing the card to be revealed if you win the draw
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="bg-gradient-to-br from-gray-400 to-gray-500 dark:from-gray-600 dark:to-gray-700 rounded-xl p-4 sm:p-6 overflow-x-auto">
          <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-13 gap-3 sm:gap-4 min-w-[480px]">
            {availableCards.map(card => {
              const isDrawn = card.drawn;
              const isSelected = selectedCard === card.id;
              const suit = card.suit?.toLowerCase();

              return (
                <button
                  key={card.id || card.cardNumber || card.num}
                  onClick={() => !isDrawn && handleSelect(card.id, card.cardNumber || card.num || card.number)}
                  disabled={isDrawn}
                  className={`aspect-[2/3] rounded-lg transition-all ${isDrawn ? 'opacity-60 cursor-not-allowed' : 'hover:scale-105 hover:shadow-2xl'}`}
                >
                  <div className="w-full h-full rounded-lg overflow-hidden relative shadow-lg border-[3px] border-white">
                    {isDrawn ? (
                      <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #eff2f8 100%)' }}>
                        <div className={`absolute top-0.5 left-1 ${suitColors[suit] || 'text-gray-600'}`}>
                          <span className="text-base sm:text-lg font-bold">{suitSymbols[suit] || ''}</span>
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className={`text-lg sm:text-xl font-bold ${suitColors[suit] || 'text-gray-600'}`}>
                            {getRank(card.number || card.cardNumber || card.num)}
                          </span>
                        </div>
                        <div className={`absolute bottom-0.5 right-1 ${suitColors[suit] || 'text-gray-600'}`}>
                          <span className="text-base sm:text-lg font-bold">{suitSymbols[suit] || ''}</span>
                        </div>
                      </div>
                    ) : isSelected ? (
                      <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #eff2f8 100%)' }}>
                        <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600 mb-1" />
                        <span className="text-blue-600 font-bold text-xs sm:text-sm">MY ENVELOPE</span>
                      </div>
                    ) : (
                      <>
                        <div
                          className="w-full h-full"
                          style={{
                            backgroundColor: '#1e40af',
                            backgroundImage: `
                              linear-gradient(45deg, rgba(30, 58, 138, 0.7) 25%, transparent 25%, transparent 50%, rgba(30, 58, 138, 0.7) 50%, rgba(30, 58, 138, 0.7) 75%, transparent 75%),
                              linear-gradient(45deg, rgba(30, 58, 138, 0.7) 25%, transparent 25%, transparent 50%, rgba(30, 58, 138, 0.7) 50%, rgba(30, 58, 138, 0.7) 75%, transparent 75%)
                            `,
                            backgroundSize: '14px 14px',
                            backgroundPosition: '0 0, 7px 7px',
                            border: '2px solid rgba(255, 255, 255, 0.3)',
                            margin: '3px'
                          }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-white font-bold text-lg sm:text-xl md:text-2xl drop-shadow-lg">
                            {card.cardNumber || card.num}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {selectedCardNum && (
        <div className="mt-4 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-semibold text-sm">
            <CheckCircle className="w-4 h-4" />
            Card #{selectedCardNum} selected
          </span>
        </div>
      )}
    </div>
  );
}
